import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { DeleteOutlined, DownOutlined } from '@ant-design/icons';
import { translate } from '@smovilgps/optimus-intl';
import { Button, Card, Col, Dropdown, Popconfirm, Row, Typography } from 'antd-v6';
import axios from 'axios';
import { useAppToast } from '@/components/AppToastProvider';
import MobileCommonTable from '../../components/MobileCommonTable';
import { isMobile } from '../../utilities/util';
import CommonTable from '../common-components/CommonTable';
import { AddDeviceModels, DeleteModels, DisableModels, EditModels, SearchModels } from './actions';
import AddButton from './AddButton';
import AddOrEditForm from './AddOrEditForm';

const { Link } = Typography;

const Models = () => {
	const intl = useIntl();
	const toast = useAppToast();
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const recordCount = useSelector((state) => state.deviceModel.count);
	const deviceModels = useSelector((state) => state.deviceModel.deviceModels);

	const [edit, setEdit] = useState(false);
	const [loading, setLoading] = useState(false);
	const [isVisible, setIsVisible] = useState(false);
	const [recordToEdit, setRecordToEdit] = useState();

	const source = axios.CancelToken.source();

	useEffect(() => {
		doPaging();
		return () => {
			source.cancel();
		};
	}, []);

	const doPaging = (search = '', page = 1, pageSize = 15) => {
		setLoading(true);
		dispatch(SearchModels(search, page, pageSize, source.token))
			.catch(() => toast.error())
			.finally(() => setLoading(false));
	};

	const deleteModels = (selectedModels) => {
		if (!!selectedModels.length) {
			dispatch(DeleteModels(selectedModels)).then((unDeleted) => {
				if (unDeleted.length > 0) toast.warning('deletePartial');
				else toast.success('deleteSuccess');
			});
		} else toast.warning('deleteZero');

		return [];
	};

	const onCancel = () => {
		setRecordToEdit();
		setIsVisible(false);
		setEdit(false);
	};

	const showModal = (record = undefined) => {
		setRecordToEdit(record);
		setIsVisible(true);
		setEdit(!!record);
	};

	const doDisableModel = (record, isActive) => {
		dispatch(DisableModels(record.id, isActive)).then(() => {
			const msg = !isActive ? 'deviceModelEnable' : 'deviceModelDisable';
			toast.success(msg, { name: record.description });
		});
	};

	const columns = [
		{
			title: intl.formatMessage(translate('name')),
			dataIndex: 'description',
			key: 'description',
		},
		{
			title: intl.formatMessage(translate('modelSendType')),
			dataIndex: ['modelSendType', 'description'],
			key: 'modelSendTypeId',
		},
		{
			title: intl.formatMessage(translate('brand')),
			dataIndex: ['brand', 'description'],
			key: 'brandId',
		},
		{
			title: intl.formatMessage(translate('active')),
			dataIndex: 'active',
			className: 'status',
			render: (active) => {
				const style = {
					color: '#CCCCCC',
					fontSize: '18px',
				};
				if (active) style.color = '#87D068';
				return <span className="icon-circle" style={style} />;
			},
		},
		{
			title: intl.formatMessage(translate('actions')),
			dataIndex: 'id',
			render: (id, record) => {
				const isActive = record.active;
				const disableText = intl.formatMessage(translate(!isActive ? 'enable' : 'disable'));
				return (
					<span>
						<Link className="editable-add-btn" onClick={() => showModal(record)}>
							<span className="icon-pencil" />
							<FormattedMessage {...translate('edit')} />
						</Link>
						<span> | </span>
						<Link onClick={() => doDisableModel(record, isActive)}>{disableText}</Link>
					</span>
				);
			},
		},
	];

	const menu = (record) => {
		const id = record.id;
		const isActive = record.active;
		const disableText = intl.formatMessage(translate(isActive ? 'disable' : 'enable'));
		const menuItems = [
			{
				key: '0',
				label: (
					<Link className="editable-add-btn" onClick={() => showModal(record)}>
						<span className="icon-pencil" />
						<FormattedMessage {...translate('edit')} />
					</Link>
				),
			},
			{
				key: '1',
				label: <Link onClick={() => doDisableModel(record, isActive)}>{disableText}</Link>,
			},
			{
				key: '2',
				label: (
					<Popconfirm
						title={intl.formatMessage(translate('deleteConfirm'))}
						onConfirm={() => deleteModels([id])}
						okText={intl.formatMessage(translate('yes'))}
						cancelText={intl.formatMessage(translate('no'))}
					>
						<Link>
							<DeleteOutlined />
							<FormattedMessage {...translate('delete')} />
						</Link>
					</Popconfirm>
				),
			},
		];
		return menuItems;
	};

	const action = (record) => {
		const items = menu(record);
		return (
			<Dropdown menu={{ items }} trigger={['click']}>
				<Link className="ant-dropdown-link" href="#">
					<FormattedMessage {...translate('actions')} />
					<DownOutlined />
				</Link>
			</Dropdown>
		);
	};

	const card = (record) => {
		const style = {
			color: '#CCCCCC',
			fontSize: '16px',
			paddingRight: '2px',
		};
		if (record.active) style.color = '#87D068';
		const title = (
			<Row align="middle">
				<Col span={14}>
					<span className="icon-circle" style={style} />
					{record.description}
				</Col>
			</Row>
		);
		return (
			<Row key={record.id} justify="space-around" align="middle">
				<Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }}>
					<Card loading={loading} title={title} extra={action(record)} style={{ width: '100%' }}>
						<Row justify="center">
							<Col span={7}>
								<strong>
									<FormattedMessage {...translate('modelSendType')} />:
								</strong>
							</Col>
							<Col span={12}>{record.modelSendType.description}</Col>
						</Row>
						<Row justify="center">
							<Col span={7}>
								<strong>
									<FormattedMessage {...translate('brand')} />:
								</strong>
							</Col>
							<Col span={12}>{record.brand.description}</Col>
						</Row>
					</Card>
				</Col>
			</Row>
		);
	};

	const addButton = <AddButton setModalVisibility={showModal} />;

	const commonTable = (
		<CommonTable
			columns={columns}
			dataSource={deviceModels}
			onSearch={(search, page, pageSize) => doPaging(search, page, pageSize)}
			onDelete={(selectedUsers) => deleteModels(selectedUsers)}
			recordCount={recordCount}
			Add={
				<span>
					{addButton}
					<Button data-testid="profile-btn" className="extraButton" onClick={() => navigate('profile')}>
						<FormattedMessage {...translate('modelProfile')} />
					</Button>
				</span>
			}
			loading={loading}
			getCheckboxProps={() => ({ disabled: false })}
		/>
	);
	const mobileCommonTable = (
		<MobileCommonTable
			dataSource={deviceModels}
			Add={addButton}
			search={(search, page, pageSize) => doPaging(search, page, pageSize)}
			recordCount={recordCount}
			loading={loading}
			card={card}
		/>
	);
	const modelsView = isMobile() ? mobileCommonTable : commonTable;

	const onSave = async (values) => {
		const func = edit ? EditModels : AddDeviceModels;
		await dispatch(func(values));
		doPaging();
	};

	return (
		<div className="models view">
			{modelsView}
			<AddOrEditForm
				title={intl.formatMessage(translate(edit ? 'editModelTitle' : 'addModelTitle'))}
				record={recordToEdit}
				onSave={onSave}
				visible={isVisible}
				onCancel={onCancel}
				setRecord={setRecordToEdit}
			/>
		</div>
	);
};

export default Models;
